import { useEffect, useState } from 'react';
import { doc, getDoc, getDocs, query, where, collection, serverTimestamp, setDoc } from 'firebase/firestore';

const CURRENT_YEAR = String(new Date().getFullYear());

export const useTeamAccess = (auth, db, teamId, isOwner) => {
  const [teamMeta, setTeamMeta] = useState(null);
  const [directoryStatus, setDirectoryStatus] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersError, setTeamMembersError] = useState('');

  // Load team meta (base + season)
  useEffect(() => {
    const loadTeamMeta = async () => {
      if (!teamId) {
        setTeamMeta(null);
        return;
      }
      try {
        const baseSnap = await getDoc(doc(db, 'teams', teamId));
        const base = baseSnap.exists() ? baseSnap.data() : {};
        let seasonData = {};
        try {
          const seasonSnap = await getDoc(doc(db, 'teams', teamId, 'seasons', CURRENT_YEAR));
          if (seasonSnap.exists()) seasonData = seasonSnap.data();
        } catch (err) {
          console.error('Failed to load season meta', err);
        }
        setTeamMeta({
          id: teamId,
          baseName: base.name || teamId,
          region: seasonData.region || base.region || 'Region',
          league: seasonData.league || base.league || 'League',
          seasonName: seasonData.displayName || seasonData.name || base.name || teamId,
        });
      } catch (err) {
        console.error('Failed to load team meta', err);
        setTeamMeta(null);
      }
    };
    loadTeamMeta();
  }, [db, teamId]);

  // Load members
  useEffect(() => {
    const loadMembers = async () => {
      if (!teamId) {
        setTeamMembers([]);
        return;
      }
      setTeamMembersLoading(true);
      setTeamMembersError('');
      try {
        const snap = await getDocs(query(collection(db, 'users'), where('teamId', '==', teamId)));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTeamMembers(rows);
      } catch (err) {
        console.error('Failed to load team members', err);
        setTeamMembersError('Unable to load team members.');
      } finally {
        setTeamMembersLoading(false);
      }
    };
    loadMembers();
  }, [db, teamId]);

  // Ensure directory entry
  useEffect(() => {
    const ensureDirectoryEntry = async () => {
      if (!isOwner || !teamId) return;
      try {
        const dirRef = doc(db, 'teamDirectory', teamId);
        const dirSnap = await getDoc(dirRef);
        if (dirSnap.exists()) return;

        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        const teamData = teamSnap.exists() ? teamSnap.data() : {};
        const seasonYear = String(teamData.currentSeasonYear || new Date().getFullYear());

        await setDoc(dirRef, {
          name: teamData.name || teamId,
          region: teamData.region || 'NA',
          season: seasonYear,
          league: teamData.league || 'Open',
          members: Array.isArray(teamData.memberIds) ? teamData.memberIds.length : teamData.members || 1,
          capacity: teamData.capacity || 20,
          status: 'request',
          description: teamData.description || '',
          ownerUid: auth?.currentUser?.uid || '',
          createdAt: serverTimestamp(),
        });
        setDirectoryStatus('Published your team to the directory so players can find it.');
      } catch (err) {
        console.error('Failed to publish directory entry', err);
        setDirectoryStatus('Could not publish team to directory. Try again later.');
      }
    };
    ensureDirectoryEntry();
  }, [auth, db, isOwner, teamId]);

  return {
    teamMeta,
    directoryStatus,
    teamMembers,
    teamMembersLoading,
    teamMembersError,
    setTeamMembers,
    setTeamMembersError,
  };
};

export default useTeamAccess;
